/** Shared ShipsGo route GeoJSON helpers (sea legs only). */

import { haversineKm } from '../pages/components/tracking/portCoords.js';

export function asGeojson(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

export function featureBlob(props) {
  const bits = [];
  const walkVal = (v, depth = 0) => {
    if (v == null || depth > 4) return;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') bits.push(String(v));
    else if (Array.isArray(v)) v.forEach((x) => walkVal(x, depth + 1));
    else if (typeof v === 'object') Object.values(v).forEach((x) => walkVal(x, depth + 1));
  };
  walkVal(props);
  return bits.join(' ').toUpperCase();
}

export function isRailwayFeature(feature) {
  return /\bRAIL(WAY|ROAD)?\b/.test(featureBlob(feature?.properties));
}

/** Longitude folded back into [-180, 180). */
export const norm180 = (lng) => ((((lng + 180) % 360) + 360) % 360) - 180;

/** `lng` moved into the same world copy as `ref` (never more than 180 away). */
export const alignLng = (ref, lng) => ref + norm180(lng - ref);

/** [lat, lng] with the longitude aligned to `ref`'s world copy. */
export const alignPointTo = (ref, pt) => (pt ? [pt[0], alignLng(ref[1], pt[1])] : pt);

/** [lat, lng] folded back into one world copy — for markers, which never repeat. */
export const wrapPoint = (pt) => (pt ? [pt[0], norm180(pt[1])] : pt);

/**
 * Longitudes made continuous: no two consecutive vertices jump more than 180.
 *
 * ShipsGo hands back legs in mixed frames — some in [-180, 180], some already
 * unwrapped the other way (Asia as -225 rather than +135). Concatenating those
 * raw puts a ~360 degree step in the middle of a polyline, which Leaflet draws
 * as a flat line clean across the map. The result here may run past +-180; that
 * is the point — splitAtDateLine cuts it back into the base copy at render time.
 *
 * @param {Array<[number,number]>} path
 * @param {number|null} anchorLng  frame to continue from; null starts a new one
 */
export function unwrapLngPath(path, anchorLng = null) {
  if (!Array.isArray(path) || path.length === 0) return [];
  let lng = anchorLng == null ? norm180(path[0][1]) : alignLng(anchorLng, path[0][1]);
  const out = [[path[0][0], lng]];
  for (let i = 1; i < path.length; i++) {
    lng = alignLng(lng, path[i][1]);
    out.push([path[i][0], lng]);
  }
  return out;
}

/**
 * Cut an unwrapped path into pieces that each stay inside one world copy, every
 * longitude folded back into [-180, 180].
 *
 * A date-line crossing becomes two pieces — one ending at +180, one starting at
 * -180 — rather than one line sweeping back across the whole map. Keeping every
 * piece in the base copy also keeps the route with its own markers, which
 * Leaflet only ever draws once.
 */
export function splitAtDateLine(path) {
  if (!Array.isArray(path) || path.length === 0) return [];
  if (path.length < 2) return [path.map(wrapPoint)];
  const copyOf = (lng) => Math.floor((lng + 180) / 360);
  const out = [];
  let cur = [wrapPoint(path[0])];
  for (let i = 1; i < path.length; i++) {
    const b = path[i];
    let from = path[i - 1];
    let copy = copyOf(from[1]);
    const target = copyOf(b[1]);
    const step = target > copy ? 1 : -1;
    // One boundary at a time, so a leg spanning several copies still splits.
    while (copy !== target && b[1] !== from[1]) {
      const edge = (copy + (step > 0 ? 1 : 0)) * 360 - 180;
      const lat = from[0] + ((edge - from[1]) / (b[1] - from[1])) * (b[0] - from[0]);
      cur.push([lat, step > 0 ? 180 : -180]);
      out.push(cur);
      cur = [[lat, step > 0 ? -180 : 180]];
      from = [lat, edge];
      copy += step;
    }
    cur.push(wrapPoint(b));
  }
  out.push(cur);
  return out.filter((seg) => seg.length >= 2);
}

export function lineToLatLngs(line) {
  if (!Array.isArray(line)) return [];
  const coords = [];
  line.forEach((pt) => {
    if (!Array.isArray(pt) || pt.length < 2) return;
    const [lng, lat] = pt;
    if (Number.isFinite(lat) && Number.isFinite(lng)) coords.push([lat, lng]);
  });
  return coords;
}

/**
 * Sea LineStrings only. Skip RAILWAY legs (e.g. Balboa→Manzanillo CURRENT).
 *
 * Legs come out in one continuous longitude frame: each one is unwrapped, and
 * its first vertex is anchored to the previous leg's last, so any later
 * concatenation (concatSeaPath, splitAroundPin) is seam-free.
 */
export function extractSeaLegs(geojson) {
  const gj = asGeojson(geojson);
  const legs = [];
  let frameLng = null;
  const addLeg = (coords, props) => {
    if (!coords || coords.length < 2) return;
    const unwrapped = unwrapLngPath(coords, frameLng);
    frameLng = unwrapped[unwrapped.length - 1][1];
    legs.push({
      coords: unwrapped,
      status: String(props?.status || props?.STATE || props?.state || '').toUpperCase(),
    });
  };
  const walk = (g, props = {}) => {
    if (!g) return;
    if (g.type === 'FeatureCollection') {
      (g.features || []).forEach((f) => walk(f, f.properties || props));
      return;
    }
    if (g.type === 'Feature') {
      if (isRailwayFeature(g)) return;
      walk(g.geometry, { ...props, ...(g.properties || {}) });
      return;
    }
    if (g.type === 'Point' || g.type === 'MultiPoint') return;
    if (g.type === 'LineString') addLeg(lineToLatLngs(g.coordinates), props);
    else if (g.type === 'MultiLineString') (g.coordinates || []).forEach((line) => addLeg(lineToLatLngs(line), props));
    else if (g.type === 'GeometryCollection') (g.geometries || []).forEach((gg) => walk(gg, props));
  };
  walk(gj, {});
  return legs;
}

export function concatSeaPath(legs) {
  const pts = [];
  (legs || []).forEach((leg) => {
    (leg.coords || []).forEach((c) => {
      const last = pts[pts.length - 1];
      if (!last || last[0] !== c[0] || last[1] !== c[1]) pts.push(c);
    });
  });
  return pts;
}

function closestOnSegment(a, b, p) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { point: a, dist: haversineKm(a, p) };
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const point = [a[0] + dx * t, a[1] + dy * t];
  return { point, dist: haversineKm(point, p) };
}

export function splitPathAtPin(path, pin) {
  if (!path.length) return { traveled: [], remaining: [] };
  if (!pin || path.length < 2) return { traveled: path, remaining: [] };
  let bestI = 0;
  let bestDist = Infinity;
  // closestOnSegment is planar, and the path may run past +-180. Compare the pin
  // in each segment's own world copy, else the search picks a vertex 360 away.
  let bestPin = alignPointTo(path[0], pin);
  for (let i = 0; i < path.length - 1; i++) {
    const local = alignPointTo(path[i], pin);
    const hit = closestOnSegment(path[i], path[i + 1], local);
    if (hit.dist < bestDist) {
      bestDist = hit.dist;
      bestI = i;
      bestPin = local;
    }
  }
  return {
    traveled: [...path.slice(0, bestI + 1), bestPin],
    remaining: [bestPin, ...path.slice(bestI + 1)],
  };
}

function bearingDeg(from, to) {
  if (!from || !to) return null;
  const lat1 = (from[0] * Math.PI) / 180;
  const lat2 = (to[0] * Math.PI) / 180;
  const dLon = ((to[1] - from[1]) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function angDiff(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Traveled / remaining at AIS. Drops FUTURE that aims >90° from cargo POD (canal vs Oxnard). */
export function splitAroundPin(legs, pin, pod) {
  const pastPath = concatSeaPath((legs || []).filter((l) => l.status !== 'FUTURE'));
  const futurePath = concatSeaPath((legs || []).filter((l) => l.status === 'FUTURE'));
  if (pin && pastPath.length >= 2) {
    const split = splitPathAtPin(pastPath, pin);
    // splitPathAtPin already moved the pin into the path's frame; reuse that one
    // so nothing prepends a raw [-180, 180] pin to an unwrapped path.
    const framedPin = split.traveled[split.traveled.length - 1];
    let remaining = split.remaining;
    const futureNear = futurePath[0] && haversineKm(framedPin, futurePath[0]) < 80;
    if (futureNear) {
      const aim = futurePath[Math.min(4, futurePath.length - 1)];
      const futBrg = bearingDeg(framedPin, aim);
      const podBrg = pod ? bearingDeg(framedPin, pod) : null;
      if (podBrg == null || futBrg == null || angDiff(futBrg, podBrg) <= 90) {
        remaining = [alignPointTo(futurePath[0], framedPin), ...futurePath];
      }
    } else if (futurePath.length >= 2) {
      // Vessel is still mid PAST leg, so FUTURE starts at the PAST/FUTURE
      // junction far ahead of the AIS fix. That leg carries the run to the
      // POD — append it instead of dropping it (path stopped at the junction).
      remaining = concatSeaPath([{ coords: remaining }, { coords: futurePath }]);
    }
    return { traveled: split.traveled, remaining };
  }
  return { traveled: pastPath, remaining: futurePath };
}

export function remainingAfterPin(legs, pin, pod) {
  return splitAroundPin(legs, pin, pod).remaining;
}
